import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiX,
  FiUsers,
  FiFolder,
  FiMessageSquare,
  FiClock,
  FiTag,
  FiPlus,
  FiLoader,
  FiSend,
  FiZap,
} from "react-icons/fi";
import { toast } from "react-toastify";
import collaboratorsAPI from "../../services/collaboratorsAPI";
import "./CollabRequestModal.css";

// Animation variants
const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 30 },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: { duration: 0.2 },
  },
};

const CollabRequestModal = ({ isOpen, onClose, targetUser, onRequestSent }) => {
  const [formData, setFormData] = useState({
    researchRoomId: "",
    message: "",
    collaborationType: "research",
    expectedDuration: "not_specified",
    skillsNeeded: [],
  });
  const [researchRooms, setResearchRooms] = useState([]);
  const [newSkill, setNewSkill] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const collaborationTypes = [
    { value: "research", label: "Research", icon: "🔬" },
    { value: "writing", label: "Writing", icon: "✍️" },
    { value: "analysis", label: "Analysis", icon: "📊" },
    { value: "experiment", label: "Experiment", icon: "🧪" },
    { value: "review", label: "Review", icon: "📝" },
    { value: "mentorship", label: "Mentorship", icon: "🎓" },
    { value: "other", label: "Other", icon: "💡" },
  ];

  const durationOptions = [
    { value: "short_term", label: "Short Term (< 1 month)" },
    { value: "medium_term", label: "Medium Term (1-6 months)" },
    { value: "long_term", label: "Long Term (6+ months)" },
    { value: "ongoing", label: "Ongoing" },
    { value: "not_specified", label: "Not Specified" },
  ];

  useEffect(() => {
    if (isOpen) {
      fetchResearchRooms();
      resetForm();
    }
  }, [isOpen]);

  const fetchResearchRooms = async () => {
    try {
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = currentUser?.id || currentUser?._id;

      if (userId) {
        const professionalProfileAPI = (
          await import("../../services/professionalProfileAPI")
        ).default;
        const rooms = await professionalProfileAPI.rooms.getUserRooms(userId);

        // Filter to only leader rooms
        const leaderRooms = rooms.filter((room) => {
          const leaderId =
            typeof room.leader === "object"
              ? room.leader?._id || room.leader?.id
              : room.leader;
          return leaderId === userId;
        });

        setResearchRooms(leaderRooms || []);
      }
    } catch (error) {
      toast.error("Failed to load research rooms");
    }
  };

  const resetForm = () => {
    setFormData({
      researchRoomId: "",
      message: "",
      collaborationType: "research",
      expectedDuration: "not_specified",
      skillsNeeded: [],
    });
    setNewSkill("");
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.researchRoomId) {
      newErrors.researchRoomId = "Please select a research room";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleAddSkill = () => {
    if (newSkill.trim() && !formData.skillsNeeded.includes(newSkill.trim())) {
      if (formData.skillsNeeded.length < 10) {
        setFormData((prev) => ({
          ...prev,
          skillsNeeded: [...prev.skillsNeeded, newSkill.trim()],
        }));
        setNewSkill("");
      }
    }
  };

  const handleRemoveSkill = (skill) => {
    setFormData((prev) => ({
      ...prev,
      skillsNeeded: prev.skillsNeeded.filter((s) => s !== skill),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setIsSubmitting(true);

      const recipientId = targetUser?.id || targetUser?._id;

      if (!recipientId) {
        toast.error("Invalid user. Please try again.");
        return;
      }

      const result =
        await collaboratorsAPI.collaboration.sendCollaborationRequest(
          recipientId,
          formData
        );

      if (result.success) {
        toast.success("Collaboration request sent!");
        onRequestSent && onRequestSent(result.data.request);
        onClose();
      }
    } catch (error) {
      toast.error(error.message || "Failed to send request");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="collab-modal-overlay"
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
        onClick={handleOverlayClick}
      >
        <motion.div
          className="collab-modal"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="collab-modal-header">
            <div className="collab-modal-header-content">
              <div className="collab-modal-icon">
                <FiUsers />
              </div>
              <div className="collab-modal-title-area">
                <h2>Request Collaboration</h2>
                <p>
                  Invite <strong>{targetUser?.name}</strong> to collaborate
                </p>
              </div>
            </div>
            <motion.button
              className="collab-modal-close"
              onClick={onClose}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FiX />
            </motion.button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="collab-modal-form">
            {/* Research Room */}
            <div className="collab-modal-field">
              <label>
                <FiFolder /> Research Room <span className="required">*</span>
              </label>
              <select
                value={formData.researchRoomId}
                onChange={(e) =>
                  handleInputChange("researchRoomId", e.target.value)
                }
                className={errors.researchRoomId ? "error" : ""}
              >
                <option value="">Select a research room</option>
                {researchRooms.map((room) => (
                  <option key={room._id || room.id} value={room._id || room.id}>
                    {room.name} ({room.memberCount || 0} members)
                  </option>
                ))}
              </select>
              {errors.researchRoomId && (
                <span className="collab-modal-error">
                  {errors.researchRoomId}
                </span>
              )}
            </div>

            {/* Collaboration Type */}
            <div className="collab-modal-field">
              <label>
                <FiZap /> Collaboration Type
              </label>
              <div className="collab-modal-type-grid">
                {collaborationTypes.map((type) => (
                  <motion.button
                    key={type.value}
                    type="button"
                    className={`collab-modal-type-btn ${
                      formData.collaborationType === type.value ? "active" : ""
                    }`}
                    onClick={() =>
                      handleInputChange("collaborationType", type.value)
                    }
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className="collab-modal-type-icon">{type.icon}</span>
                    <span>{type.label}</span>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Message */}
            <div className="collab-modal-field">
              <label>
                <FiMessageSquare /> Message <span className="required">*</span>
              </label>
              <textarea
                placeholder="Describe what you'd like to collaborate on and why you think they'd be a great fit..."
                value={formData.message}
                onChange={(e) => handleInputChange("message", e.target.value)}
                className={errors.message ? "error" : ""}
                rows={4}
                maxLength={1000}
              />
              <div className="collab-modal-char-count">
                {formData.message.length}/1000
              </div>
              {errors.message && (
                <span className="collab-modal-error">{errors.message}</span>
              )}
            </div>

            {/* Duration */}
            <div className="collab-modal-field">
              <label>
                <FiClock /> Expected Duration
              </label>
              <select
                value={formData.expectedDuration}
                onChange={(e) =>
                  handleInputChange("expectedDuration", e.target.value)
                }
              >
                {durationOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Skills */}
            <div className="collab-modal-field">
              <label>
                <FiTag /> Skills Needed (Optional)
              </label>
              <div className="collab-modal-skills-input">
                <input
                  type="text"
                  placeholder="Add a skill"
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddSkill();
                    }
                  }}
                />
                <motion.button
                  type="button"
                  onClick={handleAddSkill}
                  disabled={!newSkill.trim()}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FiPlus />
                </motion.button>
              </div>
              {formData.skillsNeeded.length > 0 && (
                <div className="collab-modal-skills-list">
                  {formData.skillsNeeded.map((skill, index) => (
                    <motion.span
                      key={index}
                      className="collab-modal-skill-tag"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                    >
                      {skill}
                      <button
                        type="button"
                        onClick={() => handleRemoveSkill(skill)}
                      >
                        <FiX />
                      </button>
                    </motion.span>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="collab-modal-actions">
              <motion.button
                type="button"
                className="collab-modal-cancel"
                onClick={onClose}
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Cancel
              </motion.button>
              <motion.button
                type="submit"
                className="collab-modal-submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isSubmitting ? (
                  <>
                    <FiLoader className="spinning" /> Sending...
                  </>
                ) : (
                  <>
                    <FiSend /> Send Request
                  </>
                )}
              </motion.button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CollabRequestModal;
